/**
 * ORCA360 Part D - Root Cause Analysis & Training Recommendations Component
 * Displays Focus Competencies 1-4 with causal factors and recommendations
 */


export interface FocusCompetency {
  focusNumber: number;
  competency: string;
  competencyName: string;
  causalFactor: string;
  recommendations: string;
  evidence?: string[];
}

export interface PartDData {
  focusCompetencies: FocusCompetency[];
  overallStrategy?: string;
  facilitatorQuestions?: string[];
}

interface Props {
  data: PartDData;
}

const COMPETENCY_LABELS: Record<string, string> = {
  PRO: 'Application of Procedures',
  COM: 'Communication',
  FPA: 'Flight Path Mgmt - Auto',
  FPM: 'Flight Path Mgmt - Manual',
  KNO: 'Knowledge',
  LTW: 'Leadership & Teamwork',
  PSD: 'Prob. Solving & Decision Making',
  SAW: 'Situation Awareness',
  WLM: 'Workload Management',
};

export function ORCA360PartD({ data }: Props) {
  if (!data.focusCompetencies || data.focusCompetencies.length === 0) {
    return (
      <div className="bg-white sm:rounded-lg sm:shadow-md p-2 sm:p-6 border-y sm:border border-slate-200 sm:border-gray-200">
        <h2 className="text-base sm:text-lg font-bold text-navy mb-2 sm:mb-4 border-b border-gray-200 pb-2">
          Part D — Root Cause Analysis & Training Recommendations
        </h2>
        <div className="p-3 sm:p-4 bg-green-50 border border-green-200 rounded text-center">
          <span className="text-green-700 font-medium text-sm">
            All competencies at or above standard. No focus areas identified.
          </span>
        </div>
        {data.overallStrategy && (
          <div className="mt-3 sm:mt-4 p-2 sm:p-3 bg-blue-50 rounded">
            <h4 className="text-sm font-medium text-blue-800 mb-1">Overall Strategy</h4>
            <p className="text-sm text-gray-700">{data.overallStrategy}</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white sm:rounded-lg sm:shadow-md p-2 sm:p-6 border-y sm:border border-slate-200 sm:border-gray-200">
      <h2 className="text-base sm:text-lg font-bold text-navy mb-2 border-b border-gray-200 pb-2">
        Part D — Root Cause Analysis & Training Recommendations
      </h2>
      <p className="text-xs sm:text-sm text-gray-500 mb-3 sm:mb-4 italic">
        (Required for each Focus Competency)
      </p>

      {/* Focus Competencies */}
      <div className="space-y-3 sm:space-y-6">
        {data.focusCompetencies.map((fc, idx) => (
          <div
            key={idx}
            className="border border-gray-200 rounded-lg overflow-hidden"
          >
            {/* Header */}
            <div className="bg-orange-50 px-3 py-2 sm:px-4 sm:py-3 border-b border-orange-200">
              <h3 className="font-semibold text-navy text-sm sm:text-base">
                Focus Competency {fc.focusNumber}
              </h3>
              <div className="flex items-center gap-2 mt-0.5 sm:mt-1">
                <span className="inline-block px-2 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                  {fc.competency}
                </span>
                <span className="text-xs sm:text-sm text-gray-700">
                  {COMPETENCY_LABELS[fc.competency] || fc.competencyName}
                </span>
              </div>
            </div>

            {/* Content */}
            <div className="p-2 sm:p-4 space-y-2 sm:space-y-4">
              {/* Why did it happen? */}
              <div>
                <h4 className="text-xs sm:text-sm font-semibold text-navy mb-1 sm:mb-2 flex items-center">
                  <span className="w-4 h-4 sm:w-5 sm:h-5 bg-red-100 text-red-700 rounded-full flex items-center justify-center text-[10px] sm:text-xs mr-1.5 sm:mr-2">?</span>
                  Why did it happen?
                </h4>
                <div className="bg-red-50 border border-red-100 rounded p-2 sm:p-3">
                  <p className="text-xs sm:text-sm text-gray-700">{fc.causalFactor}</p>
                </div>
              </div>

              {/* Recommendations */}
              <div>
                <h4 className="text-xs sm:text-sm font-semibold text-navy mb-1 sm:mb-2 flex items-center">
                  <span className="w-4 h-4 sm:w-5 sm:h-5 bg-green-100 text-green-700 rounded-full flex items-center justify-center text-[10px] sm:text-xs mr-1.5 sm:mr-2">&rarr;</span>
                  Recommendations
                </h4>
                <div className="bg-green-50 border border-green-100 rounded p-2 sm:p-3">
                  <p className="text-xs sm:text-sm text-gray-700">{fc.recommendations}</p>
                </div>
              </div>

              {/* Supporting Evidence */}
              {fc.evidence && fc.evidence.length > 0 && (
                <div>
                  <h4 className="text-xs sm:text-sm font-semibold text-gray-600 mb-1 sm:mb-2">Supporting Evidence</h4>
                  <ul className="list-disc list-inside text-xs text-gray-600 space-y-0.5 sm:space-y-1">
                    {fc.evidence.map((ev, i) => (
                      <li key={i}>{ev}</li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Overall Strategy */}
      {data.overallStrategy && (
        <div className="mt-3 sm:mt-6 p-2 sm:p-4 bg-blue-50 border border-blue-200 rounded">
          <h4 className="font-semibold text-blue-800 mb-1 sm:mb-2 text-sm">Overall Training Strategy</h4>
          <p className="text-xs sm:text-sm text-gray-700">{data.overallStrategy}</p>
        </div>
      )}

      {/* Facilitator Questions */}
      {data.facilitatorQuestions && data.facilitatorQuestions.length > 0 && (
        <div className="mt-3 sm:mt-6 p-2 sm:p-4 bg-purple-50 border border-purple-200 rounded">
          <h4 className="font-semibold text-purple-800 mb-2 sm:mb-3 text-sm">Facilitator Questions</h4>
          <ul className="space-y-1.5 sm:space-y-2">
            {data.facilitatorQuestions.map((question, idx) => (
              <li key={idx} className="flex items-start gap-1.5 sm:gap-2">
                <span className="w-4 h-4 sm:w-5 sm:h-5 bg-purple-100 text-purple-700 rounded-full flex items-center justify-center text-[10px] sm:text-xs flex-shrink-0 mt-0.5">
                  {idx + 1}
                </span>
                <span className="text-xs sm:text-sm text-gray-700">{question}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

export default ORCA360PartD;
